import { FieldProps, useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import UDSelect, { UDSelectProps } from './component';

import classNames from 'classnames';
import { Location, LocationOption } from 'modules/locations/domain/interfaces/location';
import UDFormFieldWrapper from '../../wrapper';
import { UDFormComponentProps } from '../types';
import { useSelector } from 'react-redux';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { useQuery } from 'react-query';
import locationsRepository from 'modules/locations/domain/repositories/locations.repository';
import { time } from 'modules/stream/domain/constants/time';
import _ from 'lodash';

type UDFormLocationSelectProps<isMulti extends boolean> = UDFormComponentProps &
  Omit<UDSelectProps<isMulti>, 'options'> & {
    selectComponent?: (props: any) => React.ReactElement<any, any> | null;
    formatOptionLabel?: (location: Location) => string;
    filterLocations?: (locations: Location[]) => Location[];
    availableLocations?: Location[];
    options?: LocationOption[];
  };

type Returning = <isMulti extends boolean = false>(props: UDFormLocationSelectProps<isMulti>) => JSX.Element;

const UDFormLocationSelect: Returning = (props) => {
  const {
    name,
    containerProps,
    required,
    label,
    labelProps,
    selectComponent,
    filterLocations,
    availableLocations,
    options,
    ...otherSelectProps
  } = props;

  const { setFieldValue } = useFormikContext();

  const tournamentId = useSelector(authStoreUserTournamentId);
  const { data: locations, isFetching: isLocationsFetching } = useQuery({
    queryKey: ['locations', tournamentId],
    queryFn: () => {
      if (!tournamentId) return Promise.resolve([]);
      return locationsRepository.fetchLocationsOptions({ tournamentId });
    },
    enabled: !!tournamentId,
    keepPreviousData: true,
    staleTime: 60 * time.minute,
  });

  const onChange = useCallback(
    (value: any, actionMeta: any) => {
      setFieldValue(name, value);

      if (props.onChange) {
        props.onChange(value, actionMeta);
      }
    },
    [name, props, setFieldValue],
  );

  const Component = selectComponent || UDSelect;

  return (
    <UDFormFieldWrapper name={name} containerProps={containerProps} label={label} labelProps={labelProps} required={required}>
      {({ field }: FieldProps<any>) => (
        <Component
          {...otherSelectProps}
          {...field}
          divProps={{
            className: classNames('w-full', otherSelectProps?.className),
          }}
          options={_.unionBy(locations ?? [], options ?? [], 'value')}
          onChange={onChange}
          isLoading={isLocationsFetching}
        />
      )}
    </UDFormFieldWrapper>
  );
};

export default UDFormLocationSelect;
